/**
 * Deep Link Handler for managing shareable URLs and routing
 * Handles link generation, validation, expiration, and routing
 */

import { GameSessionManager } from './GameSessionManager';

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

export interface DeepLinkData {
  type: 'join-room' | 'accept-challenge' | 'view-result' | 'general-invite';
  gameType?: string;
  roomCode?: string;
  challengeId?: string;
  resultId?: string;
  expiresAt: Date;
  metadata: Record<string, any>;
}

export interface ShareableLink {
  id: string;
  url: string;
  data: DeepLinkData;
  createdAt: Date;
  expiresAt: Date;
}

export class DeepLinkHandler {
  private static instance: DeepLinkHandler;
  private baseURL: string;
  private linkCache: Map<string, ShareableLink> = new Map();

  private constructor() {
    // Use production domain from environment, fallback to current origin for development
    this.baseURL = process.env.REACT_APP_DOMAIN || window.location.origin;
    
    // Warn if using localhost
    if (this.baseURL.includes('localhost')) {
      console.warn('⚠️ DeepLinkHandler: Using localhost domain. Links will not work in production!');
      console.warn('Set REACT_APP_DOMAIN=https://playhubgames.ai in .env for production');
    }
    
    this.initializeRouting();
  }

  public static getInstance(): DeepLinkHandler {
    if (!DeepLinkHandler.instance) {
      DeepLinkHandler.instance = new DeepLinkHandler();
    }
    return DeepLinkHandler.instance;
  }

  /**
   * Initialize deep link routing on app startup
   */
  private initializeRouting(): void {
    // Check if current URL contains a deep link
    const currentURL = window.location.href;
    const deepLinkData = this.parseDeepLink(currentURL);
    
    if (deepLinkData) {
      // Handle the deep link after a short delay to allow app initialization
      setTimeout(() => {
        this.handleDeepLink(deepLinkData);
      }, 1000);
    }
  }

  /**
   * Parse a URL to extract deep link data
   */
  public parseDeepLink(url: string): DeepLinkData | null {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const searchParams = urlObj.searchParams;

      // Handle different URL patterns
      if (pathname.startsWith('/s/')) {
        // Encrypted link format: /s/{encrypted-payload}
        const encryptedPayload = pathname.substring(3);
        return this.decryptLinkPayload(encryptedPayload);
      }

      // Handle query parameter format: ?action=join&room=ABC123
      const action = searchParams.get('action');
      if (action) {
        return this.parseQueryParameters(searchParams);
      }

      return null;
    } catch (error) {
      console.error('Failed to parse deep link:', error);
      return null;
    }
  }

  /**
   * Generate a shareable URL for specific actions
   */
  public generateShareableURL(type: DeepLinkData['type'], data: any, expirationHours: number = 24): string {
    const linkId = this.generateLinkId();
    const expiresAt = new Date(Date.now() + expirationHours * 60 * 60 * 1000);
    
    const deepLinkData: DeepLinkData = {
      type,
      expiresAt,
      metadata: {},
      ...data
    };

    const shareableLink: ShareableLink = {
      id: linkId,
      url: '',
      data: deepLinkData,
      createdAt: new Date(),
      expiresAt
    };

    // Generate encrypted URL
    const encryptedPayload = this.encryptLinkPayload(deepLinkData);
    const shareableURL = `${this.baseURL}/s/${encryptedPayload}`;
    
    shareableLink.url = shareableURL;
    this.linkCache.set(linkId, shareableLink);

    // Store in localStorage for persistence
    this.storeLinkInCache(linkId, shareableLink);

    return shareableURL;
  }

  /**
   * Handle incoming deep links and route appropriately
   */
  public async handleDeepLink(data: DeepLinkData): Promise<void> {
    try {
      // Check if link has expired
      if (new Date() > data.expiresAt) {
        this.handleExpiredLink(data);
        return;
      }

      // Route based on link type
      switch (data.type) {
        case 'join-room':
          await this.handleJoinRoom(data);
          break;
        
        case 'accept-challenge':
          await this.handleAcceptChallenge(data);
          break;
        
        case 'view-result':
          await this.handleViewResult(data);
          break;
        
        case 'general-invite':
          await this.handleGeneralInvite(data);
          break;
        
        default:
          console.warn('Unknown deep link type:', data.type);
          this.handleUnknownLink(data);
      }

      // Track link usage for analytics
      this.trackLinkUsage(data);
    } catch (error) {
      console.error('Failed to handle deep link:', error);
      this.handleLinkError(data, error);
    }
  }

  /**
   * Validate if a link is still valid
   */
  public async validateLink(linkId: string): Promise<boolean> {
    try {
      const link = this.linkCache.get(linkId) || this.getLinkFromCache(linkId);
      
      if (!link) {
        return false;
      }

      // Check expiration
      if (new Date() > link.expiresAt) {
        this.removeLinkFromCache(linkId);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Link validation failed:', error);
      return false;
    }
  }

  /**
   * Handle joining a multiplayer room
   */
  private async handleJoinRoom(data: DeepLinkData): Promise<void> {
    if (!data.roomCode || !data.gameType) {
      throw new Error('Invalid room join data');
    }

    // Navigate to the appropriate game with room code
    const gameUrl = this.buildGameURL(data.gameType, { roomCode: data.roomCode });
    
    // Use history API to navigate without page reload
    window.history.pushState({ deepLink: true }, '', gameUrl);
    
    // Dispatch custom event for app to handle
    window.dispatchEvent(new CustomEvent('deeplink-join-room', {
      detail: {
        gameType: data.gameType,
        roomCode: data.roomCode,
        metadata: data.metadata
      }
    }));
  }

  /**
   * Handle accepting a game challenge
   */
  private async handleAcceptChallenge(data: DeepLinkData): Promise<void> {
    if (!data.challengeId) {
      throw new Error('Invalid challenge data');
    }

    // Dispatch challenge acceptance event
    window.dispatchEvent(new CustomEvent('deeplink-accept-challenge', {
      detail: {
        challengeId: data.challengeId,
        gameType: data.gameType,
        metadata: data.metadata
      }
    }));
  }

  /**
   * Handle viewing game results
   */
  private async handleViewResult(data: DeepLinkData): Promise<void> {
    if (!data.resultId) {
      throw new Error('Invalid result data');
    }

    // Navigate to result view
    window.dispatchEvent(new CustomEvent('deeplink-view-result', {
      detail: {
        resultId: data.resultId,
        gameType: data.gameType,
        metadata: data.metadata
      }
    }));
  }

  /**
   * Handle general platform invites
   */
  private async handleGeneralInvite(data: DeepLinkData): Promise<void> {
    // Navigate to GameHub
    window.history.pushState({ deepLink: true }, '', '/');
    
    // Show welcome message for new users
    window.dispatchEvent(new CustomEvent('deeplink-general-invite', {
      detail: {
        metadata: data.metadata
      }
    }));
  }

  /**
   * Generate a unique link ID
   */
  private generateLinkId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Encrypt link payload (simplified implementation)
   */
  private encryptLinkPayload(data: DeepLinkData): string {
    try {
      const jsonString = JSON.stringify(data);
      // Simple base64 encoding (in production, use proper encryption)
      return btoa(jsonString).replace(/[+/=]/g, (match) => {
        switch (match) {
          case '+': return '-';
          case '/': return '_';
          case '=': return '';
          default: return match;
        }
      });
    } catch (error) {
      console.error('Failed to encrypt link payload:', error);
      throw error;
    }
  }

  /**
   * Decrypt link payload
   */
  private decryptLinkPayload(encryptedPayload: string): DeepLinkData | null {
    try {
      // Reverse the URL-safe base64 encoding
      const base64 = encryptedPayload.replace(/[-_]/g, (match) => {
        return match === '-' ? '+' : '/';
      });
      
      // Add padding if needed
      const padded = base64 + '==='.slice(0, (4 - base64.length % 4) % 4);
      
      const jsonString = atob(padded);
      const data = JSON.parse(jsonString);
      
      // Convert date strings back to Date objects
      data.expiresAt = new Date(data.expiresAt);
      
      return data;
    } catch (error) {
      console.error('Failed to decrypt link payload:', error);
      return null;
    }
  }

  /**
   * Parse query parameters into deep link data
   */
  private parseQueryParameters(searchParams: URLSearchParams): DeepLinkData | null {
    const action = searchParams.get('action');
    const gameType = searchParams.get('game');
    const roomCode = searchParams.get('room');
    const challengeId = searchParams.get('challenge');
    const resultId = searchParams.get('result');

    if (!action) return null;

    // Default expiration of 24 hours
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000);

    // Map action parameter to deep link type
    let type: DeepLinkData['type'];
    switch (action) {
      case 'join':
        type = 'join-room';
        break;
      case 'accept-challenge':
        type = 'accept-challenge';
        break;
      case 'view-result':
        type = 'view-result';
        break;
      default:
        type = 'general-invite';
    }

    const data: DeepLinkData = {
      type,
      expiresAt,
      metadata: {}
    };

    if (gameType) data.gameType = gameType;
    if (roomCode) data.roomCode = roomCode;
    if (challengeId) data.challengeId = challengeId;
    if (resultId) data.resultId = resultId;

    return data;
  }

  /**
   * Build game URL with parameters
   */
  private buildGameURL(gameType: string, params: Record<string, string>): string {
    const baseGameURL = `/${gameType}`;
    const searchParams = new URLSearchParams(params);
    return `${baseGameURL}?${searchParams.toString()}`;
  }

  /**
   * Store link in localStorage for persistence
   */
  private storeLinkInCache(linkId: string, link: ShareableLink): void {
    try {
      const cacheKey = `deeplink_${linkId}`;
      localStorage.setItem(cacheKey, JSON.stringify(link));
    } catch (error) {
      console.error('Failed to store link in cache:', error);
    }
  }

  /**
   * Retrieve link from localStorage
   */
  private getLinkFromCache(linkId: string): ShareableLink | null {
    try {
      const cacheKey = `deeplink_${linkId}`;
      const cached = localStorage.getItem(cacheKey);
      
      if (cached) {
        const link = JSON.parse(cached);
        // Convert date strings back to Date objects
        link.createdAt = new Date(link.createdAt);
        link.expiresAt = new Date(link.expiresAt);
        link.data.expiresAt = new Date(link.data.expiresAt);
        return link;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to retrieve link from cache:', error);
      return null;
    }
  }

  /**
   * Remove expired link from cache
   */
  private removeLinkFromCache(linkId: string): void {
    try {
      const cacheKey = `deeplink_${linkId}`;
      localStorage.removeItem(cacheKey);
      this.linkCache.delete(linkId);
    } catch (error) {
      console.error('Failed to remove link from cache:', error);
    }
  }

  /**
   * Handle expired links
   */
  private handleExpiredLink(data: DeepLinkData): void {
    console.warn('Deep link has expired:', data);
    
    // Show user-friendly message
    window.dispatchEvent(new CustomEvent('deeplink-expired', {
      detail: { type: data.type, gameType: data.gameType }
    }));
  }

  /**
   * Handle unknown link types
   */
  private handleUnknownLink(data: DeepLinkData): void {
    console.warn('Unknown deep link type:', data);
    
    // Fallback to general invite
    this.handleGeneralInvite(data);
  }

  /**
   * Handle link processing errors
   */
  private handleLinkError(data: DeepLinkData, error: any): void {
    console.error('Deep link processing error:', error);
    
    // Show error message to user
    window.dispatchEvent(new CustomEvent('deeplink-error', {
      detail: { type: data.type, error: error.message }
    }));
  }

  /**
   * Track link usage for analytics
   */
  private trackLinkUsage(data: DeepLinkData): void {
    try {
      // Track with existing analytics service if available
      if (window.gtag) {
        window.gtag('event', 'deeplink_used', {
          link_type: data.type,
          game_type: data.gameType || 'unknown'
        });
      }
      
      // Store usage data locally for reporting
      const usageData = {
        type: data.type,
        gameType: data.gameType,
        timestamp: new Date().toISOString()
      };
      
      const existingUsage = JSON.parse(localStorage.getItem('deeplink_usage') || '[]');
      existingUsage.push(usageData);
      
      // Keep only last 100 entries
      if (existingUsage.length > 100) {
        existingUsage.splice(0, existingUsage.length - 100);
      }
      
      localStorage.setItem('deeplink_usage', JSON.stringify(existingUsage));
    } catch (error) {
      console.error('Failed to track link usage:', error);
    }
  }

  /**
   * Clean up expired links from cache
   */
  public cleanupExpiredLinks(): void {
    try {
      const now = new Date();
      const keysToRemove: string[] = [];
      
      // Check memory cache
      const linkEntries = Array.from(this.linkCache.entries());
      for (const [linkId, link] of linkEntries) {
        if (now > link.expiresAt) {
          keysToRemove.push(linkId);
        }
      }
      
      // Remove expired links
      keysToRemove.forEach(linkId => {
        this.removeLinkFromCache(linkId);
      });
      
      // Check localStorage cache
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('deeplink_')) {
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              const link = JSON.parse(cached);
              if (now > new Date(link.expiresAt)) {
                localStorage.removeItem(key);
              }
            }
          } catch (error) {
            // Remove corrupted entries
            localStorage.removeItem(key);
          }
        }
      }
    } catch (error) {
      console.error('Failed to cleanup expired links:', error);
    }
  }
}

// Export singleton instance
export const deepLinkHandler = DeepLinkHandler.getInstance();

// Initialize cleanup on app start
if (typeof window !== 'undefined') {
  // Clean up expired links on app start
  setTimeout(() => {
    deepLinkHandler.cleanupExpiredLinks();
  }, 2000);
  
  // Set up periodic cleanup (every hour)
  setInterval(() => {
    deepLinkHandler.cleanupExpiredLinks();
  }, 60 * 60 * 1000);
}